import { useEffect, useId, useRef, useState, type ReactNode, type MouseEvent } from 'react';

import { createPortal } from 'react-dom';

import { cn } from '../../utils';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Clases extra para el panel del modal (override de tamaño, fondo,
   * paddings custom). El primitive ya aplica `w-full` + `rounded-lg` +
   * `bg-tkf-surface` + `sizeStyles[size]`; el className adicional se
   * concatena al final vía `cn()`, lo que permite overrides como
   * `max-w-[min(28rem,90vw)]` para garantizar fit en mobile.
   */
  className?: string;
}

const sizeStyles = {
  // eslint-disable-next-line no-restricted-syntax -- modal sm size, no flow wrapper
  sm: 'max-w-[385px]',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-4xl',
};

const openModalPanels: HTMLDivElement[] = [];
let previousBodyOverflow = '';

function getFocusableElements(panel: HTMLDivElement) {
  return Array.from(
    panel.querySelectorAll<HTMLElement>(
      'a[href], area[href], button, input, select, textarea, iframe, summary, [tabindex], [contenteditable="true"]'
    )
  ).filter(element => {
    if (
      element.tabIndex < 0 ||
      element.matches(':disabled, input[type="hidden"]') ||
      element.closest('[hidden], [inert]')
    ) {
      return false;
    }

    for (let current: HTMLElement | null = element; current; current = current.parentElement) {
      const style = window.getComputedStyle(current);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      if (current instanceof HTMLDetailsElement && !current.open) {
        const summary = Array.from(current.children).find(child => child.tagName === 'SUMMARY');
        if (!summary?.contains(element)) return false;
      }
      if (current === panel) break;
    }

    return true;
  });
}

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  children,
  footer,
  className,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- gate createPortal/document access until after hydration to avoid SSR mismatch
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !isMounted || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openModalPanels.at(-1) === modalRef.current) onClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isMounted, closeOnEscape, onClose]);

  // Manage focus after the portal exists; only the top modal captures keyboard navigation.
  useEffect(() => {
    const panel = modalRef.current;
    if (!isOpen || !isMounted || !panel) return;

    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (openModalPanels.length === 0) {
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    openModalPanels.push(panel);
    (getFocusableElements(panel)[0] ?? panel).focus();

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || openModalPanels.at(-1) !== panel) return;

      const focusableElements = getFocusableElements(panel);
      const first = focusableElements[0];
      const last = focusableElements.at(-1);
      const activeElement = document.activeElement;

      if (!first || !last) {
        event.preventDefault();
        panel.focus();
      } else if (!focusableElements.includes(activeElement as HTMLElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => {
      document.removeEventListener('keydown', handleTab);
      const wasTopModal = openModalPanels.at(-1) === panel;
      const panelIndex = openModalPanels.indexOf(panel);
      if (panelIndex !== -1) openModalPanels.splice(panelIndex, 1);

      if (openModalPanels.length === 0) document.body.style.overflow = previousBodyOverflow;
      if (wasTopModal && previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen, isMounted]);

  const handleOverlayClick = (e: MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isMounted || !isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descriptionId : undefined}
    >
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-tkf-overlay-dark transition-opacity animate-fade-in"
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={cn(
          'relative w-full rounded-lg border border-tkf-line-light bg-tkf-surface shadow-xl',
          'focus:outline-none',
          'animate-scale-in',
          sizeStyles[size],
          className
        )}
        tabIndex={-1}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-start justify-between p-6 pb-0">
            <div>
              {title && (
                <h2 id={titleId} className="text-lg font-semibold text-tkf-fg">
                  {title}
                </h2>
              )}
              {description && (
                <p id={descriptionId} className="mt-1 text-sm text-tkf-fg-muted">
                  {description}
                </p>
              )}
            </div>
            {showCloseButton && (
              <button
                type="button"
                className="ml-4 rounded-md p-1 text-tkf-fg-subtle hover:bg-text-alpha-5 hover:text-tkf-fg focus:outline-none focus:ring-2 focus:ring-primary-500"
                onClick={onClose}
                aria-label="Cerrar"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-6">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 border-t border-tkf-line-light px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
